import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const copyEmail = () => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText("sales@northwesttitlealliance.com")
      .then(() => toast.success("Email address copied — paste it into your mail app."))
      .catch(() => toast.info("Email us at sales@northwesttitlealliance.com"));
  } else {
    toast.info("Email us at sales@northwesttitlealliance.com");
  }
};

const EASE = [0.16, 1, 0.3, 1];

export const Contact = () => {
  return (
    <section id="contact" className="relative bg-navy-deep py-24 md:py-36 grain" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="md:col-span-6"
        >
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright">04 / Contact</span>
          <h2 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-fog mt-6 mb-10">
            Clear the file<span className="text-tech">.</span>
            <br />
            <span className="text-fog/40">Start the conversation.</span>
          </h2>

          <div className="border border-steel/30 bg-navy/60 p-8 md:p-10" data-testid="contact-email-panel">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4 text-tech-bright" />
              <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/40">Sales Inbox</span>
            </div>
            <a
              href="mailto:sales@northwesttitlealliance.com"
              onClick={copyEmail}
              data-testid="contact-sales-email"
              className="font-display font-semibold text-lg sm:text-2xl lg:text-[1.65rem] leading-snug text-fog hover:text-tech-bright transition-colors duration-300 break-all"
            >
              sales@northwesttitlealliance.com
            </a>
            <p className="text-sm text-fog/50 leading-relaxed mt-5 max-w-md">
              Tell us about your operation and which product you're evaluating —
              someone from our team will respond within one business day.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="md:col-span-5 md:col-start-8 flex flex-col justify-between gap-12"
        >
          <p className="font-display font-light text-2xl md:text-[2rem] leading-snug text-fog/80">
            Talk to the people who built the brain —{" "}
            <span className="text-tech-bright">not a ticket queue.</span>
          </p>

          <div className="space-y-8">
            <div className="border-l-2 border-tech pl-6">
              <div className="flex items-center gap-2 mb-1">
                <Phone className="h-4 w-4 text-tech-bright" />
                <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/40">Sales</span>
              </div>
              <a href="tel:+12534531754" data-testid="contact-sales-phone" className="font-display text-2xl md:text-3xl text-fog hover:text-tech-bright transition-colors duration-300">
                (253) 453-1754
              </a>
            </div>
            <div className="border-l-2 border-steel pl-6">
              <div className="flex items-center gap-2 mb-1">
                <Phone className="h-4 w-4 text-steel" />
                <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/40">Main Office</span>
              </div>
              <a href="tel:+12535407333" data-testid="contact-office-phone" className="font-display text-2xl md:text-3xl text-fog hover:text-tech-bright transition-colors duration-300">
                (253) 540-7333
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
